/**
 * Service-credential request contracts.
 *
 * ONE schema, both sides: the controller validates with it via `zodPipe`, and
 * the issue dialog validates with it via `zodResolver`. Two copies would let
 * the form accept something the API refuses — which the operator experiences as
 * a form that submits and then fails.
 */

import { z } from 'zod';

/** v1 vocabulary. An enum, so a typo is a 400 and never a silent grant. */
export const serviceCredentialScopeSchema = z.enum(['cohort:read']);

/**
 * Maximum lifetime, mirrored from the service. 90 days ≈ one quarter: short
 * enough to force four conscious renewals a year, long enough that the operator
 * does not route around the control by never revoking anything.
 */
export const SERVICE_CREDENTIAL_MAX_EXPIRY_DAYS = 90;

export const issueServiceCredentialSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    scopes: z.array(serviceCredentialScopeSchema).min(1),
    cohortMode: z.enum(['all', 'list']),
    cohortIds: z.array(z.uuid()).optional(),
    expiresInDays: z.number().int().min(1).max(SERVICE_CREDENTIAL_MAX_EXPIRY_DAYS),
  })
  // FAIL CLOSED at the boundary: `list` with an empty selection would mint a
  // credential the guard denies — a dead token whose cause is invisible to the
  // operator who created it. And `cohortIds` alongside `all` is a contradiction
  // worth rejecting rather than silently ignoring.
  .refine((v) => v.cohortMode !== 'list' || (v.cohortIds?.length ?? 0) > 0, {
    message: 'Select at least one cohort, or scope the credential to all cohorts',
    path: ['cohortIds'],
  })
  .refine((v) => v.cohortMode !== 'all' || (v.cohortIds?.length ?? 0) === 0, {
    message: 'cohortIds must be empty when the credential covers all cohorts',
    path: ['cohortIds'],
  });

export type IssueServiceCredentialRequest = z.infer<typeof issueServiceCredentialSchema>;

export const revokeServiceCredentialSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export type RevokeServiceCredentialRequest = z.infer<typeof revokeServiceCredentialSchema>;
